import React, { useState, useMemo, useRef, useCallback } from 'react';
import Checkbox from '@mui/material/Checkbox';
import {
    Box, Typography, TextField, IconButton, CircularProgress, Tooltip, useTheme, useMediaQuery, Paper, Collapse
} from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Dropdown, { DropdownOption } from './ui/dropdown';
import { DatePicker } from '@mui/x-date-pickers';
import { useSettings } from '../providers/settings-provider';

import ActionMenu from './action-menu';
import Toggle from './ui/toggle';
import dayjs from 'dayjs';
import TablePlaceholder from './table-placeholder';
import { SlidersHorizontal } from 'lucide-react';
import { getDynamicFontSize } from '../util/font';
import CopyToClipboard from './copy-to-clipboard';
import { usePermissions } from '../providers/permissions-provider';
import { SectionLabel } from './section';

export type Column = {
    id: string;
    label: string;
    size?: number;
    type?: 'string' | 'number' | 'date' | 'dropdown' | 'toggle' | 'actions' | 'checkbox' | 'currency' | 'custom';
    customElement?: (value: any, row: Record<string, any>) => React.ReactNode;
    customHeader?: (col: Column) => React.ReactNode;
    inlineEdit?: boolean;
    options?: DropdownOption[];
    buttons?: { label: string; onClick: (row: Record<string, any>) => void }[];
    onToggle?: (row: Record<string, any>, value: boolean) => void;
    onSort?: (column: string) => void;
    onEdit?: (row: Record<string, any>) => void;
    onDelete?: (row: Record<string, any>) => void;
    canEdit?: (row: Record<string, any>) => boolean;
    canDelete?: (row: Record<string, any>) => boolean;
    editLabel?: string | ((row: Record<string, any>) => string);
    deleteLabel?: string | ((row: Record<string, any>) => string);
    showTime?: boolean;
    // type: 'date' only. Text shown instead of a blank cell when this row
    // has no value for the date (e.g. "No end date" for an ongoing
    // recurring item) - applies on both the desktop table and the mobile
    // card, since both render through the same date-formatting branch.
    emptyDateLabel?: React.ReactNode;
    tooltip?: (row: Record<string, any>) => string | null;
    filterable?: boolean;
    filterComponent?: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: Record<string, any>) => void, currentFilter?: any) => React.ReactNode;
    isFilterActive?: (filterState?: any) => boolean;
    headerColor?: string;
    amountColor?: string;
    showTooltip?: boolean;
    // Renders a row-selection checkbox directly beside the actions menu in
    // this column's own cell (same look as the transactions table), instead
    // of needing a separate checkbox column with its own cell padding.
    showCheckbox?: boolean;
    // Per-row gate on the showCheckbox checkbox - e.g. a synthetic summary
    // row that shouldn't itself be selectable. Defaults to selectable.
    canSelect?: (row: Record<string, any>) => boolean;
    // type: 'actions' only. Renders extra per-row content (e.g. a status
    // icon) before the checkbox/ActionMenu in this column's own cell - same
    // idea as the Transactions page's Action column showing its Linked/Moved
    // icons inline, since this column type otherwise has a fixed
    // checkbox+ActionMenu cell with no room for customElement.
    actionsPrefix?: (row: Record<string, any>) => React.ReactNode;
    // Mobile card list only (see DynamicTableVirtual's isMobile branch).
    // Omits this column from the card entirely - for detail that isn't worth
    // the vertical space on a phone (e.g. a column already implied by
    // something shown as the card's title).
    hideOnMobile?: boolean;
    // Mobile card list only. Marks this as one of the handful of fields
    // shown up front on the card - everything else collapses behind a
    // "Show more" toggle, mirroring transactions-table-mobile.tsx's card
    // design rather than dumping every column at once. When no column in a
    // table sets this, every (non-hidden) column is treated as primary, so
    // existing callers keep their current all-visible behavior until they
    // opt into curating it.
    mobilePrimary?: boolean;
    // Mobile card list only. Controls left-to-right order among primary
    // fields (lower first) independent of this column's position in the
    // desktop table - e.g. showing the row's "identity" field (a
    // description) before short stat fields that come after it on desktop.
    // Columns without this keep their desktop array order, sorted after any
    // explicitly ordered ones.
    mobileOrder?: number;
    // Mobile card list only. Primary-row stat blocks default to their
    // natural content width and never grow or shrink - right for short,
    // fixed-format values (a badge, a currency amount) that would otherwise
    // get padded with dead space or squeezed for no reason. Set this on the
    // one field that's actually variable-length (e.g. a description) so it
    // alone absorbs the row's available width and truncates (ellipsis) if
    // it doesn't fit, instead of every block reserving an equal fixed share
    // regardless of what it actually needs.
    mobileShrink?: boolean;
    // Mobile card list only. Flex-grow/shrink weight for a mobileShrink
    // column (default 1) - only meaningful when mobileShrink is set.
    mobileFlex?: number;
    // Mobile card list only. Fixed pixel width for this stat block, so it
    // sits at the same x-position on every card regardless of how long that
    // row's particular value is - without this, a variable-length field
    // (mobileShrink or not) shifts its neighbors left/right per row, and the
    // list reads as a ragged pile of cards instead of a table. Takes
    // priority over mobileShrink/mobileFlex when set. Content still
    // truncates (ellipsis) if it doesn't fit.
    mobileWidth?: number;
    // Mobile card list only. Text alignment for this stat block's label and
    // value (default 'left') - e.g. a currency amount reading right-aligned
    // like a real numeric column.
    mobileAlign?: 'left' | 'right' | 'center';
    // Mobile card list only. Skips the small uppercase label above this
    // column's value - for a field that's already self-explanatory at a
    // glance (e.g. a colored recurrence badge), where printing its column
    // label too would just be wasted space on a narrow card.
    mobileHideLabel?: boolean;
    // Mobile card list only. Overrides customElement/the default
    // value-formatting for this column's mobile rendering - for a desktop
    // control that doesn't work at card width (e.g. a fixed-width inline
    // dropdown), swap in a plain read-only value instead.
    mobileElement?: (value: any, row: Record<string, any>) => React.ReactNode;
}

export type AdditionalAction = {
    label: string | ((row: Record<string, any>) => string);
    icon?:  'edit' | 'delete' | 'add' | 'info' | 'download' | 'upload' | 'close' | 'save' | 'warning' | 'check' | 'user' | 'send' | 'share' | 'fastForward' | 'eye' | 'link' | ((row: Record<string, any>) => 'edit' | 'delete' | 'add' | 'info' | 'download' | 'upload' | 'close' | 'save' | 'warning' | 'check' | 'user' | 'send' | 'share' | 'fastForward' | 'eye' | 'link');
    onClick: (row: Record<string, any>) => void;
    // Hides the action entirely - reserve for business-state reasons (e.g. this
    // action never applies to this row), not access control. For access control,
    // use `disabled` instead, so the action stays visible.
    visible?: (row: Record<string, any>) => boolean;
    disabled?: boolean | ((row: Record<string, any>) => boolean);
    // Shown on hover when disabled, to explain why the action isn't available
    // for this row.
    tooltip?: string | ((row: Record<string, any>) => string | undefined);
}

type DynamicTableVirtualProps = {
    columns: Column[];
    data: Record<string, any>[];
    // Sizes the table (and its Paper) to the sum of its columns' own `size`
    // instead of stretching to fill the page - for a table whose columns
    // have narrow, fixed-feeling content (e.g. Clients), that stops a wide
    // screen spreading them out into a row of floating text with no visual
    // table left. Off by default so every other caller keeps today's
    // full-width behavior.
    fitContent?: boolean;
    // Expandable rows, like the Documents page's tables: a chevron column on
    // desktop, and the panel inside the card's "Show more" area on mobile.
    renderDetailPanel?: (row: Record<string, any>) => React.ReactNode;
    getRowCanExpand?: (row: Record<string, any>) => boolean;
    title?: string;
    // Teal corner label on the table's own card (section.tsx's SectionLabel),
    // for a page laid out as labelled sections like the settings pages.
    sectionLabel?: string;
    header?: React.ReactNode;
    additionalActions?: AdditionalAction[];
    contained?: boolean;
    sortState?: { column: string; direction: 'ASC' | 'DESC' };
    fontSx?: (row: Record<string, any>) => {
        fontSize?: number;
        fontWeight?: number;
        color?: string;
    };
    customRender?: (row: Record<string, any>, columnId: string) => React.ReactNode;
    // Mobile card list only. Overrides the default flat gray card
    // background/border - e.g. the same tinted background + accent border
    // transactions-table-mobile.tsx uses per row type, alternated by idx, so
    // a page whose rows all represent one "kind" of thing (all provisions,
    // all invoices) can carry that same color identity into its cards
    // instead of every page's mobile list looking like a generic gray pile.
    mobileCardStyle?: (row: Record<string, any>, idx: number) => { background?: string; border?: string };
    // Hides the horizontally-scrolling row of per-column sort/filter chips
    // this component renders above its mobile card list by default. Set
    // this when the page already provides its own combined "Sort & Filter"
    // bottom sheet covering the same columns (see provisions.tsx/
    // invoices.tsx) - otherwise the two end up duplicating the exact same
    // controls in two different places on the same screen.
    hideMobileFilterBar?: boolean;
    maxHeight?: number;
    maxWidth?: number | string;
    isLoading?: boolean;
    onSelectionChange?: (selectedIndices: number[]) => void;
    selectedIndices?: number[];
    columnFilters?: Record<string, any>;
    onColumnFilterChange?: (filters: Record<string, any>) => void;
    headerTextColor?: string;
    itemSize?: number;
    onReorder?: (reorderedData: Record<string, any>[]) => void;
    enableRowDragAndDrop?: boolean;
    // Disables (not hides) the built-in Edit/Delete row actions and inline-edit
    // fields - for gating on a granular permission (e.g. can(resource, 'update'))
    // in addition to the base admin/non-admin check.
    canEditRows?: boolean;
    canDeleteRows?: boolean;
    // 'container' (default): the table gets its own bounded, internally-scrolling
    // box (maxHeight + overflow) with virtualization for large datasets - the
    // sticky header only helps while scrolling *within that box*, so if the
    // page itself is taller than the viewport, scrolling the page carries the
    // whole box (header included) off-screen. 'page': for small tables where
    // virtualization isn't needed - the table flows naturally with the page
    // and its header sticks to the real page scroll instead (same technique
    // Topbar already uses), so it stays visible until the table's own bottom
    // edge scrolls past the viewport, which is what "sticky" actually means
    // on a page with other content above/below the table.
    scrollMode?: 'container' | 'page';
    // How far below the viewport top this table's sticky header should sit in
    // 'page' mode - needs to clear any other sticky element above it (e.g.
    // Topbar, which is ~65px). Ignored in 'container' mode.
    stickyOffset?: number;
    // Infinite-scroll loading - only wired up in 'page' mode (that mode has no
    // bounded scroll box of its own, see scrollMode doc, so this listens on
    // window scroll instead of a container). All three are optional and
    // no-op when omitted, so existing callers are unaffected.
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    // Fires when a row is clicked anywhere outside its interactive cells
    // (checkbox, actions menu, inline-edit fields - those already stop
    // propagation so this never fires from picking a dropdown value,
    // toggling a switch, etc.). Optional and off by default, so existing
    // callers are unaffected.
    onRowClick?: (row: Record<string, any>) => void;
};

const DynamicTableVirtual: React.FC<DynamicTableVirtualProps> = ({
    columns,
    data,
    title,
    sectionLabel,
    header,
    additionalActions,
    fontSx,
    contained = true,
    customRender,
    mobileCardStyle,
    hideMobileFilterBar = false,
    sortState,
    maxHeight = 'calc(100vh - 230px)',
    maxWidth,
    isLoading = false,
    onSelectionChange,
    selectedIndices,
    columnFilters = {},
    onColumnFilterChange,
    headerTextColor = 'var(--color-default-text)',
    onReorder,
    enableRowDragAndDrop = false,
    canEditRows,
    canDeleteRows,
    scrollMode = 'container',
    stickyOffset = 65,
    hasNextPage = false,
    onLoadMore,
    isLoadingMore = false,
    onRowClick,
    fitContent = false,
    renderDetailPanel,
    getRowCanExpand,
}) => {
    const isPageScroll = scrollMode === 'page';
    const theme = useTheme();
    // Below this width, MaterialReactTable's fixed columns just get squeezed/
    // clipped or force horizontal scroll - swapped for a card-per-row layout
    // instead (see the mobile branch near the end of this component), reusing
    // the exact same column definitions/value-formatting as the desktop table
    // so every one of this shared component's ~9 callers gets mobile support
    // for free instead of needing their own bespoke mobile table.
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { formatDate, formatAmount } = useSettings();
    // Admins can always edit/delete. Anyone else only when the page grants it
    // through canEditRows/canDeleteRows (its role's Update/Delete permission for
    // that resource) - it used to also require being an admin, so a custom role
    // that HAD Update/Delete still saw every row action disabled ("You don't
    // have permission to do this"). A page that doesn't pass them stays
    // admin-only. Actions stay visible either way and are just disabled,
    // rather than removed, when access is restricted.
    // Uses the server-verified per-entity admin flag (usePermissions), not
    // User.role from the login cookie - that field is unrelated to entity-level
    // admin status (it's set to 'admin' for every signed-up account regardless
    // of their actual role within any given entity) and was letting read-only
    // users through as if they were admins.
    const { isAdmin } = usePermissions();
    const editDisabled = !isAdmin && !canEditRows;
    const deleteDisabled = !isAdmin && !canDeleteRows;
    const [sort, setSort] = useState<{ column: string; direction: 'ASC' | 'DESC' }>({ column: '', direction: 'ASC' });
    const [editData, setEditData] = useState<Record<string, any>[]>(data);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [activeFilterPopup, setActiveFilterPopup] = useState<string>('');
    // Mobile card list only - which cards have their secondary (non-primary)
    // fields expanded, keyed by row index within editData.
    const [expandedMobileRows, setExpandedMobileRows] = useState<Set<number>>(new Set());
    const [draggedRowIndex, setDraggedRowIndex] = useState<number | null>(null);
    const [dragOverRowIndex, setDragOverRowIndex] = useState<number | null>(null);
    const filterRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const rowVirtualizerInstanceRef = useRef<any>(null);

    const currentSort = sortState || sort;

    React.useEffect(() => {
        setEditData(data);
        setSelectedRows(selectedIndices || []);
    }, [data, selectedIndices]);

    // 'page' mode has no bounded scroll box of its own (rows aren't
    // virtualized there, see enableRowVirtualization below), so the trigger
    // has to watch window scroll rather than a container ref. The mobile
    // card list below is always page-flowed too (no MRT, no bounded box),
    // regardless of the caller's chosen scrollMode, so it needs the same
    // window-scroll trigger.
    React.useEffect(() => {
        if ((!isPageScroll && !isMobile) || !onLoadMore) return;
        const handleScroll = () => {
            const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
            if (nearBottom && hasNextPage && !isLoadingMore) onLoadMore();
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isPageScroll, isMobile, onLoadMore, hasNextPage, isLoadingMore]);



    const handleSelectRow = useCallback((idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSelection = e.target.checked
            ? [...selectedRows, idx]
            : selectedRows.filter(i => i !== idx);
        setSelectedRows(newSelection);
        onSelectionChange?.(newSelection);
    }, [selectedRows, onSelectionChange]);

    const handleChange = useCallback((rowIdx: number, colId: string, value: any) => {
        setEditData(prev => prev.map((row, idx) =>
            idx === rowIdx ? { ...row, [colId]: value } : row
        ));
    }, []);

    const handleFilterClick = useCallback((colId: string) => {
        const column = columns.find(c => c.id === colId);
        if (column?.filterable && column?.filterComponent) {
            setActiveFilterPopup(activeFilterPopup === colId ? '' : colId);
        } else if (column?.onSort) {
            column.onSort(colId);
        }
    }, [columns, activeFilterPopup]);

    const handleDragStart = useCallback((rowIndex: number) => {
        setDraggedRowIndex(rowIndex);
    }, []);

    const handleDragOver = useCallback((rowIndex: number, e: React.DragEvent) => {
        e.preventDefault();
        setDragOverRowIndex(rowIndex);
    }, []);

    const handleDrop = useCallback((targetRowIndex: number, e: React.DragEvent) => {
        e.preventDefault();
        if (draggedRowIndex !== null && draggedRowIndex !== targetRowIndex && enableRowDragAndDrop) {
            const newData = [...editData];
            const draggedItem = newData[draggedRowIndex];
            newData.splice(draggedRowIndex, 1);
            
            // Adjust insert index based on drag direction
            const insertIndex = draggedRowIndex < targetRowIndex ? targetRowIndex - 1 : targetRowIndex;
            
            newData.splice(insertIndex, 0, draggedItem);
            setEditData(newData);
            onReorder?.(newData);
        }
        setDraggedRowIndex(null);
        setDragOverRowIndex(null);
    }, [draggedRowIndex, editData, enableRowDragAndDrop, onReorder]);

    const handleDragEnd = useCallback(() => {
        setDraggedRowIndex(null);
        setDragOverRowIndex(null);
    }, []);

    const handleApplyFilter = useCallback((colId: string, filters: Record<string, any>) => {
        const newFilters = { ...columnFilters, [colId]: filters };
        onColumnFilterChange?.(newFilters);
        setActiveFilterPopup('');
    }, [columnFilters, onColumnFilterChange]);

    const isColumnFilterActive = useCallback((column: Column): boolean => {
        if (column.isFilterActive) {
            return column.isFilterActive(columnFilters[column.id]);
        }
        return false;
    }, [columnFilters]);

    // Shared between the desktop MRT cell renderer and the mobile card list
    // below, so both render an 'actions' column's checkbox+menu identically
    // instead of the two drifting apart over time.
    const renderActionsCell = useCallback((col: Column, rowData: Record<string, any>, rowIndex: number, layout: 'row' | 'column' = 'row', includeCheckbox: boolean = true) => {
        const menuActions = [
            ...(col.onEdit && (!col.canEdit || col.canEdit(rowData)) ? [{
                label: typeof col.editLabel === 'function' ? col.editLabel(rowData) : (col.editLabel || 'Edit'),
                icon: 'edit' as const,
                onClick: () => col.onEdit!(rowData),
                disabled: editDisabled,
            }] : []),
            ...(col.onDelete && (!col.canDelete || col.canDelete(rowData)) ? [{
                label: typeof col.deleteLabel === 'function' ? col.deleteLabel(rowData) : (col.deleteLabel || 'Delete'),
                icon: 'delete' as const,
                onClick: () => col.onDelete!(rowData),
                disabled: deleteDisabled,
            }] : []),
            ...(additionalActions?.filter(action => action.visible ? action.visible(rowData) : true).map(action => ({
                label: typeof action.label === 'function' ? action.label(rowData) : action.label,
                icon: typeof action.icon === 'function' ? action.icon(rowData) : action.icon,
                onClick: () => action.onClick(rowData),
                disabled: typeof action.disabled === 'function' ? action.disabled(rowData) : !!action.disabled,
                tooltip: typeof action.tooltip === 'function' ? action.tooltip(rowData) : action.tooltip,
            })) || [])
        ];
        return (
            <Box
                display="flex"
                flexDirection={layout}
                alignItems="center"
                justifyContent="flex-end"
                gap={layout === 'column' ? '4px' : '5px'}
                onClick={(e) => e.stopPropagation()}
            >
                {col.actionsPrefix && col.actionsPrefix(rowData)}
                {includeCheckbox && col.showCheckbox && (!col.canSelect || col.canSelect(rowData)) && (
                    <Checkbox
                        checked={selectedRows.includes(rowIndex)}
                        onChange={handleSelectRow(rowIndex)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 20, height: 20, p: 0 }}
                    />
                )}
                {/* Nothing the row can do (e.g. the system GST tax, which can't be
                    edited or deleted) - no button at all rather than an empty menu. */}
                {menuActions.length > 0 && <ActionMenu actions={menuActions} />}
            </Box>
        );
    }, [selectedRows, handleSelectRow, editDisabled, deleteDisabled, additionalActions]);

    // Shared between the desktop MRT cell renderer and the mobile card list -
    // every value-formatting branch (dropdown/date/toggle/currency/tooltip
    // etc.) lives here once instead of being duplicated per render target.
    const renderCellValue = useCallback((col: Column, rowData: Record<string, any>, rowIndex: number): React.ReactNode => {
        const cellValue = rowData[col.id];
        let content: React.ReactNode = null;

        if (col.customElement) {
            content = col.customElement(cellValue, rowData);
        } else if (col.type === 'custom') {
            content = customRender ? customRender(rowData, col.id) : null;
        } else if (col.type === 'dropdown' && col.inlineEdit) {
            content = (
                <Dropdown
                    options={col.options || []}
                    value={cellValue ?? ''}
                    onChange={val => {
                        handleChange(rowIndex, col.id, val);
                        // rowData is the pre-change row (handleChange's
                        // setEditData update hasn't been applied yet when
                        // this callback runs) - onEdit needs to actually
                        // see the value that was just picked, or a save
                        // handler consuming the row silently persists the
                        // old one.
                        col.onEdit?.({ ...rowData, [col.id]: val });
                    }}
                    placeholder="Select..."
                    isPrimary
                    height={24}
                    disabled={editDisabled}
                />
            );
        } else if (col.type === 'date' && col.inlineEdit) {
            content = (
                <DatePicker
                    value={cellValue ? dayjs(cellValue) : null}
                    onChange={val => handleChange(rowIndex, col.id, val ? val.toISOString() : '')}
                    slotProps={{ textField: { fullWidth: true, size: 'small', variant: 'outlined' } }}
                    disabled={editDisabled}
                />
            );
        } else if (col.type === 'toggle') {
            content = (
                <Toggle
                    checked={cellValue ?? false}
                    onChange={val => {
                        handleChange(rowIndex, col.id, val);
                        col.onToggle && col.onToggle(rowData, val);
                    }}
                    disabled={editDisabled}
                />
            );
        } else if (col.inlineEdit) {
            content = (
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    type={col.type === 'number' ? 'number' : 'text'}
                    value={cellValue ?? ''}
                    onChange={e => handleChange(rowIndex, col.id, col.type === 'number' ? Number(e.target.value) : e.target.value)}
                    disabled={editDisabled}
                />
            );
        } else if (col.type === 'date') {
            const dateContent = (
                <Typography sx={{ ...fontSx?.(rowData), fontWeight: (rowData as any).isTotal ? 700 : 400, ...(cellValue ? {} : { color: 'var(--color-text-muted)', fontStyle: 'italic' }) }}>
                    {cellValue ? formatDate(cellValue, col.showTime) : (col.emptyDateLabel ?? null)}
                </Typography>
            );
            const tooltipText = col.tooltip ? col.tooltip(rowData) : null;
            content = tooltipText ? (
                <Tooltip title={<Typography fontSize={13} color="black">{tooltipText}</Typography>} slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: 13, p: 2, backgroundColor: 'rgba(255, 255, 255, 1)', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' } } }} arrow>
                    <Box sx={{ cursor: 'pointer' }}>{dateContent}</Box>
                </Tooltip>
            ) : dateContent;
        } else if (col.type === 'currency') {
            const isNegative = cellValue < 0;
            const textColor = col.amountColor || (isNegative ? 'var(--color-negative-amount)' : 'var(--color-default-text)');
            const formatted = formatAmount ? formatAmount(cellValue) : String(cellValue);
            content = <Typography sx={{ ...fontSx?.(rowData), fontWeight: (rowData as any).isTotal ? 700 : 400, color: textColor }}>{formatted}</Typography>;
        } else {
            if (col.showTooltip) {
                content = (
                    <Tooltip
                        title={<Box display="flex" alignItems="center" gap={2}><Typography fontSize={13} color="black">{cellValue}</Typography><CopyToClipboard text={cellValue} /></Box>}
                        slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: 13, p: 2, color: 'black', backgroundColor: 'rgba(255, 255, 255, 1)', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' } } }}
                    >
                        <Typography sx={{ ...fontSx?.(rowData), fontWeight: (rowData as any).isTotal ? 700 : 400 }}>{cellValue}</Typography>
                    </Tooltip>
                );
            } else {
                // Ellipsis (not a hard pixel clip) once a value outgrows its column - a no-op in
                // the default stretch-to-fit layout, where columns already grow to fit their
                // content, but needed once a table is sized to its columns' own widths
                // (fitContent/layoutMode="grid"), where a flex cell otherwise clips text mid-
                // character with no "..." at all (flex items need min-width: 0 for text-overflow
                // to take effect, since their default min-width lets content overflow instead of
                // triggering it).
                content = (
                    <Typography
                        title={typeof cellValue === 'string' ? cellValue : undefined}
                        sx={{ ...fontSx?.(rowData), fontWeight: (rowData as any).isTotal ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', minWidth: 0 }}
                    >
                        {cellValue}
                    </Typography>
                );
            }
        }

        // Interactive editable cells (dropdown/date/text inline-edit,
        // toggle) need their clicks to stay on the input, not fall
        // through to the row's own onClick - e.g. picking a category
        // from the inline dropdown must not also open a row-click
        // modal underneath it.
        return (col.inlineEdit || col.type === 'toggle') ? (
            <Box onClick={(e) => e.stopPropagation()} sx={{ width: '100%' }}>{content}</Box>
        ) : content;
    }, [customRender, handleChange, editDisabled, fontSx, formatDate, formatAmount]);

    // Mobile card list only - lets a column swap in a plain read-only value
    // (via mobileElement) instead of a desktop control that assumes far more
    // width than a card row has (e.g. a fixed-width inline dropdown).
    const renderMobileCellValue = useCallback((col: Column, rowData: Record<string, any>, rowIndex: number): React.ReactNode => {
        if (col.mobileElement) return col.mobileElement(rowData[col.id], rowData);
        return renderCellValue(col, rowData, rowIndex);
    }, [renderCellValue]);

    // Build MRT column definitions
    const tableColumns = useMemo<MRT_ColumnDef<Record<string, any>>[]>(() => {
        return columns.map(col => {
            // Clicking the label is now equivalent to clicking the dedicated
            // sort arrow (both just toggle sort by this column) - the filter
            // icon is a fully separate control that only ever opens the
            // filter popover, never sorts by itself. The popover's own
            // "Sort By X" option still works too, for parity with whichever
            // entry point someone's used to.
            const triggerSort = () => {
                if (!col.onSort) return;
                if (!sortState) {
                    const newDirection = currentSort.column === col.id && currentSort.direction === 'ASC' ? 'DESC' : 'ASC';
                    setSort({ column: col.id, direction: newDirection });
                }
                col.onSort(col.id);
            };
            const header = () => (
                <Box ref={(el: HTMLDivElement | null) => { if (el && col.filterable) filterRefs.current[col.id] = el; }} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        onClick={col.onSort ? triggerSort : undefined}
                        sx={col.onSort ? { cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'var(--color-primary)' } } : undefined}
                    >
                        {col.label}
                    </Typography>
                    {col.onSort && (
                        <IconButton
                            size="small"
                            style={{
                                marginLeft: 4,
                                color: currentSort.column === col.id ? 'var(--color-primary)' : '#B0B0B0',
                                backgroundColor: currentSort.column === col.id ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                                borderRadius: '50px',
                                width: 24,
                                height: 24,
                            }}
                            tabIndex={-1}
                            aria-label="Sort"
                            onClick={triggerSort}
                        >
                            {currentSort.column === col.id ? (
                                currentSort.direction === 'ASC'
                                    ? <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>▲</Typography>
                                    : <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-primary)' }}>▼</Typography>
                            ) : (
                                <Typography sx={{ fontSize: '12px', opacity: 0.4, color: 'var(--color-text-muted)' }}>▲</Typography>
                            )}
                        </IconButton>
                    )}
                    {col.filterable && col.filterComponent && (
                        <>
                            <IconButton
                                size="small"
                                style={{
                                    marginLeft: 4,
                                    color: isColumnFilterActive(col) ? 'var(--color-primary)' : '#B0B0B0',
                                    backgroundColor: isColumnFilterActive(col) ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                                    borderRadius: '50px',
                                    width: 24,
                                    height: 24,
                                }}
                                tabIndex={-1}
                                aria-label="Filter"
                                onClick={() => handleFilterClick(col.id)}
                            >
                                <Box display="flex" alignItems="center">
                                    {/* A column with no onSort (e.g. Actions) has nothing to
                                    show a sort triangle FOR - showing one anyway (the old
                                    behavior below) made a filter-only button look like a
                                    sort control. Always show the filter icon for those; a
                                    sortable column still skips it here since its own
                                    dedicated sort button (just to the left) already shows
                                    the arrow, and repeating it here would be redundant. */}
                                    <SlidersHorizontal size={12} color={isColumnFilterActive(col) ? 'var(--color-primary)' : 'var(--color-text-muted)'} />
                                </Box>
                            </IconButton>
                            {/* Keeps key presses inside the (portalled) filter popup
                                away from MaterialReactTable's header-cell keyboard
                                navigation, which preventDefault()s arrow/Home/End keys
                                and would stop the caret moving in the popup's fields. */}
                            <Box component="span" sx={{ display: 'contents' }} onKeyDown={(e) => e.stopPropagation()}>
                                {col.filterComponent(
                                    activeFilterPopup === col.id,
                                    filterRefs.current[col.id] || null,
                                    () => setActiveFilterPopup(''),
                                    (filters) => handleApplyFilter(col.id, filters),
                                    columnFilters[col.id]
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            );

            const cellRenderer = ({ cell, row }: any) => {
                const rowData = row.original;

                if (col.type === 'checkbox') {
                    return (
                        <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                checked={selectedRows.includes(row.index)}
                                onChange={handleSelectRow(row.index)}
                                sx={{ width: 20, height: 20, p: 0 }}
                            />
                        </Box>
                    );
                }

                if (col.type === 'actions') {
                    return <Box width="100%">{renderActionsCell(col, rowData, row.index)}</Box>;
                }

                if (col.type === 'currency') {
                    return <Box display="flex" alignItems="center" justifyContent="flex-end" pr={2} width="100%">{renderCellValue(col, rowData, row.index)}</Box>;
                }

                return renderCellValue(col, rowData, row.index);
            };

            // In grid layout mode, MRT turns each column's `size` into BOTH its flex-basis
            // and its flex-grow weight (`flex: size 0 auto`), so every sized column stretches
            // proportionally to its own size once the table is wider than its columns' sum -
            // the same uneven "redistribute leftover width across everything" behavior as the
            // default layout, just via flexbox instead of a native table. Overriding `flex`
            // here pins every explicitly-sized column to that exact width (grow: 0) and lets
            // only the one column with no declared size (e.g. Address) absorb the slack.
            const gridFlexSx = fitContent
                ? (typeof col.size === 'number'
                    ? { flex: `0 0 ${col.size}px`, minWidth: col.size }
                    // No hard floor here - a fixed minWidth can exceed the space actually left
                    // over once the pinned columns claim theirs at narrower widths, forcing the
                    // row to overflow its container instead of shrinking. minWidth: 0 lets
                    // flex-shrink compress it as needed (the cell's own Typography already
                    // handles the ellipsis once it does).
                    : { flex: '1 1 180px', minWidth: 0 })
                : undefined;

            return {
                accessorKey: col.id,
                header: col.label,
                Header: header,
                Cell: cellRenderer,
                enableSorting: false,
                size: typeof col.size === 'number' ? col.size : undefined,
                muiTableHeadCellProps: {
                    sx: {
                        fontWeight: 600,
                        color: col.headerColor || headerTextColor,
                        fontSize: getDynamicFontSize(col.label, col.size || 150, 12),
                        backgroundColor: 'var(--color-container-background)',
                        border: 'none',
                        // Above the virtualized body rows scrolling underneath it -
                        // without this, fast scrolling can flash a row through the
                        // sticky header for a frame.
                        zIndex: 2,
                        ...gridFlexSx,
                    }
                },
                muiTableBodyCellProps: {
                    sx: { ...gridFlexSx }
                },
            };
        });
    }, [columns, currentSort, sortState, columnFilters, selectedRows, headerTextColor, activeFilterPopup, handleApplyFilter, handleFilterClick, handleSelectRow, isColumnFilterActive, renderActionsCell, renderCellValue, fitContent]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* transactions-table-mobile.tsx's own wrapper uses zero padding
                and sits flush against the page - this component's desktop
                p:4 panel padding, left in place on mobile too, is what was
                making the card list look boxed-in with wide margins instead
                of edge-to-edge like that reference. */}
            {/* On mobile, cards need a canvas that actually contrasts with
                their own (often near-white/pale-tinted) background - a page
                background here washes everything into a single white mass
                with only faint borders between rows instead of distinct
                cards. */}
            <Paper sx={{ width: '100%', maxWidth: '100%', borderRadius: 0, background: isMobile ? 'var(--color-container-background)' : 'var(--color-page-background)', boxShadow: isMobile ? 'none' : undefined, p: contained ? (isMobile ? 0 : 4) : 0, boxSizing: 'border-box', position: 'relative' }}>
                {sectionLabel && !isMobile && <SectionLabel title={sectionLabel} />}
                {/* No room for the corner label above a mobile card list -
                    a plain heading says which section the cards belong to. */}
                {sectionLabel && isMobile && (
                    <Typography sx={{ px: 1, pt: 1, pb: 0.5, fontSize: 14, fontWeight: 700, color: 'var(--color-primary)' }}>
                        {sectionLabel}
                    </Typography>
                )}
                {header ? header : title && <Box mb={2}><Typography variant="h6" color='var(--color-text-muted)'>{title}</Typography></Box>}


                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }} fontSize={16}>
                            Loading data...
                        </Typography>
                    </Box>
                ) : isMobile ? (
                    // Bottom room so the last cards can scroll clear of a page's floating + button.
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pb: 9 }}>
                        {/* Same horizontal scrolling chip row as the Transactions
                            page's mobile card list - reuses the exact sort/filter
                            state and filterComponent popups the desktop header
                            already wires up, just rendered compactly instead of
                            as MRT column headers. */}
                        {!hideMobileFilterBar && columns.some(col => col.onSort || (col.filterable && col.filterComponent)) && (
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.75, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
                                {columns.filter(col => col.onSort || (col.filterable && col.filterComponent)).map(col => (
                                    <Box
                                        key={col.id}
                                        ref={(el: HTMLDivElement | null) => { if (el) filterRefs.current[col.id] = el; }}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.75, px: 1, bgcolor: 'var(--color-container-background)', width: 'fit-content', flexShrink: 0, borderRadius: 1 }}
                                    >
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--color-default-text)', whiteSpace: 'nowrap' }}>
                                            {col.label}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleFilterClick(col.id)}
                                            sx={{ p: 0.25, color: 'var(--color-primary)' }}
                                        >
                                            <Box display="flex" alignItems="center" gap={0.25}>
                                                {isColumnFilterActive(col) && <SlidersHorizontal size={11} color="var(--color-primary)" />}
                                                {col.onSort ? (
                                                    currentSort.column === col.id ? (
                                                        <Typography sx={{ fontSize: 11, fontWeight: 'bold', color: 'var(--color-primary)' }}>
                                                            {currentSort.direction === 'ASC' ? '▲' : '▼'}
                                                        </Typography>
                                                    ) : (
                                                        <Typography sx={{ fontSize: 10, opacity: 0.4, color: 'var(--color-text-muted)' }}>▼</Typography>
                                                    )
                                                ) : (
                                                    // No onSort (e.g. Actions) - nothing to show a sort
                                                    // triangle for, so this chip is filter-only. Skip a
                                                    // second sliders icon when the one above already
                                                    // covers the active-filter case.
                                                    !isColumnFilterActive(col) && <SlidersHorizontal size={11} color="var(--color-text-muted)" />
                                                )}
                                            </Box>
                                        </IconButton>
                                        {col.filterable && col.filterComponent && col.filterComponent(
                                            activeFilterPopup === col.id,
                                            filterRefs.current[col.id] || null,
                                            () => setActiveFilterPopup(''),
                                            (filters) => handleApplyFilter(col.id, filters),
                                            columnFilters[col.id]
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {editData.length === 0 ? (
                            <TablePlaceholder
                                title="No Data Available"
                                message="No data available for the selected filters."
                            />
                        ) : (
                            editData.map((rowData, idx) => {
                                const actionsCol = columns.find(c => c.type === 'actions');
                                const checkboxCol = columns.find(c => c.type === 'checkbox');
                                const allDisplayColumns = columns.filter(c => c.type !== 'actions' && c.type !== 'checkbox' && !c.hideOnMobile);
                                // When no column opts into mobilePrimary, keep the old
                                // all-visible behavior rather than guessing which fields
                                // matter for a table this component hasn't been curated for.
                                const anyPrimarySet = allDisplayColumns.some(c => c.mobilePrimary);
                                const primaryColumns = (anyPrimarySet ? allDisplayColumns.filter(c => c.mobilePrimary) : allDisplayColumns)
                                    .slice()
                                    .sort((a, b) => (a.mobileOrder ?? Number.MAX_SAFE_INTEGER) - (b.mobileOrder ?? Number.MAX_SAFE_INTEGER));
                                const secondaryColumns = anyPrimarySet ? allDisplayColumns.filter(c => !c.mobilePrimary) : [];
                                const isExpanded = expandedMobileRows.has(idx);
                                const canSelectRow = checkboxCol ? true : (actionsCol?.showCheckbox && (!actionsCol.canSelect || actionsCol.canSelect(rowData)));
                                // Label-above-value blocks, same typography as
                                // transactions-table-mobile.tsx's card (small
                                // uppercase muted label, bold value below it) -
                                // primary fields sit side by side in a row,
                                // secondary ones stack full-width once expanded.
                                const renderStatBlock = (col: Column) => (
                                    <Box
                                        key={col.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            ...(col.mobileWidth
                                                ? { flex: `0 0 ${col.mobileWidth}px`, width: col.mobileWidth }
                                                : { flex: col.mobileShrink ? `0 ${col.mobileFlex ?? 1} auto` : '0 0 auto', minWidth: col.mobileShrink ? 0 : undefined }),
                                        }}
                                    >
                                        {!col.mobileHideLabel && (
                                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#707070', textTransform: 'uppercase', mb: 0.5, textAlign: col.mobileAlign || 'left' }}>
                                                {col.label}
                                            </Typography>
                                        )}
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                                textAlign: col.mobileAlign || 'left',
                                                color: 'var(--color-default-text)',
                                                // A stat block only has room for one line - without
                                                // this, a value wider than its flex share (e.g. a
                                                // long description next to two short badges) paints
                                                // straight over its neighbors instead of shrinking.
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                            }}
                                            component="div"
                                        >
                                            {renderMobileCellValue(col, rowData, idx)}
                                        </Typography>
                                    </Box>
                                );
                                const renderDetailBlock = (col: Column) => (
                                    <Box key={col.id} sx={{ mb: 1 }}>
                                        {!col.mobileHideLabel && (
                                            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#707070', textTransform: 'uppercase', mb: 0.5 }}>
                                                {col.label}
                                            </Typography>
                                        )}
                                        <Typography sx={{ fontSize: 13, color: 'var(--color-default-text)' }} component="div">
                                            {renderMobileCellValue(col, rowData, idx)}
                                        </Typography>
                                    </Box>
                                );
                                const cardStyle = mobileCardStyle?.(rowData, idx);
                                // Same zebra convention the desktop table already uses
                                // (muiTableBodyRowProps below) - without alternating,
                                // every card on a page that hasn't set its own
                                // mobileCardStyle renders the identical flat gray as
                                // the canvas behind it, and the whole list reads as
                                // one undifferentiated block instead of distinct rows.
                                const defaultCardBg = idx % 2 !== 0 ? 'var(--color-container-background)' : 'var(--color-page-background)';
                                return (
                                    <Paper
                                        key={idx}
                                        elevation={0}
                                        onClick={onRowClick ? () => onRowClick(rowData) : undefined}
                                        sx={{
                                            py: 0.5,
                                            px: 1,
                                            borderRadius: 1,
                                            background: (rowData as any).isTotal ? `${theme.palette.primary.main}40` : (cardStyle?.background ?? defaultCardBg),
                                            border: `1px solid ${cardStyle?.border ?? 'rgba(0, 0, 0, 0.08)'}`,
                                            boxShadow: 'none',
                                            boxSizing: 'border-box',
                                            cursor: onRowClick ? 'pointer' : 'default',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ flex: 1, display: 'flex', gap: 1, minWidth: 0 }}>
                                                {primaryColumns.map(renderStatBlock)}
                                            </Box>
                                            {(actionsCol?.showCheckbox || checkboxCol) && canSelectRow && (
                                                <Checkbox
                                                    checked={selectedRows.includes(idx)}
                                                    onChange={handleSelectRow(idx)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ width: 20, height: 20, p: 0 }}
                                                />
                                            )}
                                            {/* No "Show more" row to share with (nothing to expand) -
                                                the action menu has nowhere else to go, so it stays
                                                up here next to the checkbox. */}
                                            {actionsCol && secondaryColumns.length === 0 && renderActionsCell(actionsCol, rowData, idx, 'row', false)}
                                        </Box>
                                        {secondaryColumns.length > 0 && (
                                            <>
                                                <Collapse in={isExpanded} timeout={0} unmountOnExit>
                                                    <Box sx={{ pt: 1, mt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                                        {secondaryColumns.map(renderDetailBlock)}
                                                        {renderDetailPanel && (getRowCanExpand?.(rowData) ?? true) && (
                                                            <Box onClick={(e) => e.stopPropagation()}>{renderDetailPanel(rowData)}</Box>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedMobileRows(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(idx)) next.delete(idx); else next.add(idx);
                                                                return next;
                                                            });
                                                        }}
                                                        sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-primary)', fontSize: 12, lineHeight: 1.4 }}
                                                    >
                                                        {isExpanded ? 'Show less' : 'Show more'}
                                                    </Box>
                                                    {/* The checkbox already sits alone up in the header -
                                                        this shares the "Show more" row with just the menu
                                                        button instead of stacking under the checkbox. */}
                                                    {actionsCol && renderActionsCell(actionsCol, rowData, idx, 'row', false)}
                                                </Box>
                                            </>
                                        )}
                                    </Paper>
                                );
                            })
                        )}
                        {isLoadingMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 2 }}>
                                <CircularProgress size={20} />
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading...</Typography>
                            </Box>
                        )}
                        {onLoadMore && !isLoadingMore && !isLoading && editData.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1.5 }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                                    {hasNextPage ? 'Scroll for more' : "You've reached the end"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <MaterialReactTable
                            columns={tableColumns}
                            data={editData}
                            layoutMode={fitContent ? 'grid' : undefined}
                            {...(renderDetailPanel ? {
                                enableExpanding: true,
                                enableExpandAll: false,
                                getRowCanExpand: (row: { original: Record<string, any> }) => getRowCanExpand?.(row.original) ?? true,
                                renderDetailPanel: ({ row }: { row: MRT_Row<Record<string, any>> }) => renderDetailPanel(row.original),
                                // Same as the Documents tables: a chevron-sized column
                                // that doesn't grow under grid layout, no tooltip text.
                                displayColumnDefOptions: { 'mrt-row-expand': { size: 40, grow: false, muiTableHeadCellProps: { sx: { flex: '0 0 40px', minWidth: 40, maxWidth: 40, px: 0, background: 'var(--color-container-background)' } }, muiTableBodyCellProps: { sx: { flex: '0 0 40px', minWidth: 40, maxWidth: 40, px: 0 } } } },
                                localization: { expand: '', collapse: '', expandAll: '', collapseAll: '' },
                                muiDetailPanelProps: { sx: { width: '100%', display: 'block', boxSizing: 'border-box' } },
                            } : {})}
                            enableRowVirtualization={!isPageScroll}
                            rowVirtualizerInstanceRef={isPageScroll ? undefined : rowVirtualizerInstanceRef}
                            rowVirtualizerOptions={isPageScroll ? undefined : { overscan: 20 }}
                            enablePagination={false}
                            enableTopToolbar={false}
                            enableBottomToolbar={false}
                            enableColumnActions={false}
                            // 'container' mode: sticky relative to this table's own bounded
                            // scroll box (muiTableContainerProps below) - fine as long as that
                            // box never has to scroll away as part of a taller page. 'page'
                            // mode: sticky relative to the real page scroll instead (see
                            // scrollMode doc on the props type), same technique Topbar already
                            // uses successfully - this is what actually keeps the header
                            // visible when the table isn't the only thing on the page.
                            enableStickyHeader
                            initialState={{ density: 'compact' }}
                            // Keeps the column header visible when there's no data instead of
                            // swapping the whole table out for a placeholder - the header row
                            // is what tells the user what this table is even for.
                            renderEmptyRowsFallback={() => (
                                <TablePlaceholder
                                    title="No Data Available"
                                    message="No data available for the selected filters."
                                />
                            )}
                            muiTableContainerProps={{
                                sx: isPageScroll ? {
                                    // MRT's own MRT_TableContainer applies a default
                                    // maxHeight: clamp(350px, calc(100vh - toolbarHeight), 9999px)
                                    // whenever enableStickyHeader is set (which this component
                                    // always does) - that default survives unless explicitly
                                    // overridden here, capping the table to roughly one
                                    // viewport's height no matter how many rows it has. Fine
                                    // when content fits under that cap (looks like it "shrinks
                                    // correctly"), but once row count grows past it the table
                                    // gets its own internal scrollbar instead of growing with
                                    // the page the way 'page' mode is supposed to.
                                    maxHeight: 'none',
                                    maxWidth: maxWidth || '100%',
                                    overflow: 'visible',
                                } : {
                                    maxHeight: maxHeight,
                                    maxWidth: maxWidth || '100%',
                                    overflow: 'auto',
                                    position: 'relative'
                                },
                                ref: containerRef
                            }}
                            muiTablePaperProps={{ sx: { boxShadow: 'none', overflow: 'visible' } }}
                            muiTableHeadProps={{
                                sx: {
                                    position: 'sticky',
                                    top: isPageScroll ? stickyOffset : 0,
                                    zIndex: 2,
                                    ...(fitContent && { gridTemplateColumns: 'minmax(0, 1fr)' }),
                                }
                            }}
                            // Grid layout's body/head are CSS grids whose implicit column sizes
                            // to the rows' content - pinned to the table's width instead, so a
                            // row wider than the card shrinks its unsized column rather than
                            // running past the edge.
                            muiTableBodyProps={fitContent ? { sx: { gridTemplateColumns: 'minmax(0, 1fr)' } } : undefined}
                            muiTableHeadRowProps={{
                                sx: {
                                    position: 'sticky',
                                    top: isPageScroll ? stickyOffset : 0,
                                    zIndex: 2,
                                    // Grid rows otherwise take the sum of their columns' sizes
                                    // and run past the card when that's wider than the space -
                                    // held to the table's width, the unsized column shrinks instead.
                                    ...(fitContent && { width: '100%' }),
                                }
                            }}
                            muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                            muiTableBodyRowProps={({ row }: { row: MRT_Row<Record<string, any>> }) => ({
                                draggable: enableRowDragAndDrop,
                                onDragStart: () => handleDragStart(row.index),
                                onDragOver: (e) => handleDragOver(row.index, e),
                                onDrop: (e) => handleDrop(row.index, e),
                                onDragEnd: handleDragEnd,
                                onClick: onRowClick ? () => onRowClick(row.original) : undefined,
                                sx: {
                                    backgroundColor: dragOverRowIndex === row.index && draggedRowIndex !== null
                                        ? 'rgba(0, 182, 155, 0.2)'
                                        : (row.original as any).isTotal
                                        ? `${theme.palette.primary.main}40`
                                        : row.index % 2 !== 0 ? 'var(--color-container-background)' : 'var(--color-page-background)',
                                    fontWeight: (row.original as any).isTotal ? 600 : 400,
                                    opacity: draggedRowIndex === row.index ? 0.5 : 1,
                                    cursor: enableRowDragAndDrop ? 'grab' : (onRowClick ? 'pointer' : 'default'),
                                    ...(fitContent && { width: '100%' }),
                                    '&:hover': enableRowDragAndDrop ? { backgroundColor: dragOverRowIndex === row.index ? 'rgba(0, 182, 155, 0.2)' : 'rgba(0, 182, 155, 0.1)' } : {},
                                }
                            })}
                        />
                        {isLoadingMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 2 }}>
                                {/* Fixed pixel size (not relative/percentage-based) - MUI's
                                    indeterminate CircularProgress animates its own arc length
                                    on a loop regardless, but that's easy to miss as a "loading"
                                    signal once the page has grown tall from many loaded rows.
                                    The label makes it unambiguous either way. */}
                                <CircularProgress size={20} />
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading...</Typography>
                            </Box>
                        )}
                        {/* Infinite-scroll status - only for callers that actually opted
                            into pagination (onLoadMore provided), and only once there's
                            something to report a status about (data.length > 0, not the
                            empty-state case) and nothing else is already loading. */}
                        {onLoadMore && !isLoadingMore && !isLoading && data.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1.5 }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                                    {hasNextPage ? 'Scroll for more' : "You've reached the end"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
        </LocalizationProvider>
    );
};

export default DynamicTableVirtual;
